<template>
    <div class="tw:px-6 tw:py-6">
        <!-- Error state -->
        <div v-if="error" class="tw:mb-6 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-lg tw:p-4 tw:flex tw:items-center tw:gap-3">
            <span class="tw:text-sm tw:text-red-700">{{ error }}</span>
            <Button variant="danger" @click="refresh">
                {{ $t('back.dashboard.retry') }}
            </Button>
        </div>

        <!-- Loading timeout error -->
        <div v-if="loadingTimedOut" class="tw:mb-6 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-lg tw:p-4 tw:flex tw:items-center tw:gap-3">
            <span class="tw:text-sm tw:text-red-700">{{ $t('back.dashboard.occupancy.loading_error') || 'La carga está tomando más de lo esperado.' }}</span>
            <Button variant="danger" @click="refresh">
                {{ $t('back.dashboard.retry') }}
            </Button>
        </div>

        <!-- Loading skeleton -->
        <template v-if="loading && !loadingTimedOut">
            <div class="tw:flex tw:flex-col tw:items-center tw:mb-8">
                <div class="tw:w-40 tw:h-40 tw:rounded-full tw:bg-gray-200 tw:animate-pulse"></div>
            </div>
            <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white">
                <div class="tw:h-64 tw:bg-gray-100 tw:animate-pulse tw:rounded"></div>
            </div>
        </template>

        <!-- Data -->
        <template v-else-if="data">
            <!-- Section title -->
            <div class="tw:flex tw:items-center tw:gap-2 tw:mb-1">
                <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900">{{ $t('back.dashboard.occupancy.section_title') }}</h3>
                <Badge v-if="isMultiSeason" variant="info" :label="$t('back.dashboard.comparison')" />
            </div>
            <p class="tw:text-sm tw:text-gray-500 tw:mb-6">{{ $t('back.dashboard.occupancy.section_description') }}</p>

            <!-- Gauge + per-jornada line chart -->
            <div class="tw:grid tw:grid-cols-1 lg:tw:grid-cols-3 tw:gap-6 tw:mb-8">
                <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white tw:flex tw:flex-col tw:items-center tw:justify-center">
                    <template v-if="isMultiSeason && seasonBreakdown?.length">
                        <div class="tw:text-center tw:w-full">
                            <h4 class="tw:text-xs tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wider tw:mb-4">{{ $t('back.dashboard.occupancy.avg_label') }}</h4>
                            <div
                                v-for="row in seasonBreakdown"
                                :key="row.season"
                                class="tw:flex tw:justify-between tw:items-center tw:py-1.5 tw:px-2"
                            >
                                <span class="tw:text-sm tw:text-gray-500">{{ row.season }}</span>
                                <span class="tw:text-lg tw:font-bold tw:text-gray-800">{{ formatPercent(row.avgOccupancy) }}</span>
                            </div>
                            <div
                                v-if="seasonDelta !== null"
                                class="tw:flex tw:justify-between tw:items-center tw:py-1.5 tw:px-2 tw:border-t tw:border-gray-100 tw:mt-1"
                            >
                                <span class="tw:text-sm tw:text-gray-500">{{ $t('back.dashboard.delta') }}</span>
                                <span :class="deltaClass">{{ seasonDelta >= 0 ? '+' : '' }}{{ seasonDelta.toFixed(1) }} %</span>
                            </div>
                        </div>
                    </template>
                    <GaugeDisplay
                        v-else
                        :percentage="gaugeValue"
                        :size="180"
                        :label="$t('back.dashboard.occupancy.avg_label')"
                    />
                </div>
                <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white lg:tw:col-span-2">
                    <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.occupancy.per_jornada_title') }}</h3>
                    <p class="tw:text-sm tw:text-gray-500 tw:mb-4">{{ $t('back.dashboard.occupancy.per_jornada_desc') }}</p>
                    <Chart
                        v-if="perJornadaChartData.length > 1"
                        type="LineChart"
                        :data="perJornadaChartData"
                        :options="lineChartOptions"
                        :height="280"
                    />
                    <div v-else class="tw:h-64 tw:flex tw:items-center tw:justify-center tw:text-gray-400 tw:text-sm">
                        {{ $t('back.dashboard.no_data') }}
                    </div>
                </div>
            </div>

            <!-- Multi-season: comparison column chart + breakdown -->
            <template v-if="isMultiSeason">
                <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white tw:mb-8">
                    <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.comparison_title') }}</h3>
                    <p class="tw:text-sm tw:text-gray-500 tw:mb-4">{{ $t('back.dashboard.occupancy.comparison_desc') }}</p>
                    <Chart
                        v-if="seasonComparisonData.length > 1"
                        type="ColumnChart"
                        :data="seasonComparisonData"
                        :options="comparisonChartOptions"
                        :height="280"
                    />
                    <div v-else class="tw:h-64 tw:flex tw:items-center tw:justify-center tw:text-gray-400 tw:text-sm">
                        {{ $t('back.dashboard.no_data') }}
                    </div>
                </div>

                <!-- Season breakdown table -->
                <div v-if="seasonBreakdown?.length" class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-4 tw:bg-white">
                    <h4 class="tw:text-sm tw:font-semibold tw:text-gray-700 tw:mb-3">{{ $t('back.dashboard.economic.breakdown_title') }}</h4>
                    <table class="tw:w-full tw:text-sm">
                        <thead>
                            <tr class="tw:text-left tw:text-gray-500 tw:text-xs">
                                <th class="tw:pb-2">{{ $t('back.dashboard.season') }}</th>
                                <th class="tw:pb-2 tw:text-right">{{ $t('back.dashboard.occupancy.avg_label') }}</th>
                                <th class="tw:pb-2 tw:text-right">{{ $t('back.dashboard.occupancy.per_jornada_title') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in seasonBreakdown" :key="row.season" class="tw:border-t tw:border-gray-100">
                                <td class="tw:py-1.5">{{ row.season }}</td>
                                <td class="tw:py-1.5 tw:text-right">{{ formatPercent(row.avgOccupancy) }}</td>
                                <td class="tw:py-1.5 tw:text-right">{{ row.matchCount }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>
        </template>

        <!-- Empty state -->
        <div v-else class="tw:bg-gray-50 tw:border tw:border-gray-200 tw:rounded-lg tw:p-8 tw:text-center">
            <p class="tw:text-sm tw:text-gray-500">{{ $t('back.dashboard.no_data') }}</p>
        </div>
    </div>
</template>

<script setup>
    import { computed, toRef, watch, ref, onUnmounted } from 'vue';
    import { useOccupancy } from '@/composables/admin/dashboard/useOccupancy';
    import { formatPercent } from '@/utils/format';
    import GaugeDisplay from '@/modules/admin/ui/GaugeDisplay.vue';
    import Chart from '@/modules/admin/ui/Chart.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import Badge from '@/modules/admin/ui/Badge.vue';

    const props = defineProps({
        seasonIds: { type: Array, required: true },
        seasons: { type: Array, required: true },
    });


    const seasonIdsRef = toRef(props, 'seasonIds');
    const seasonsRef = toRef(props, 'seasons');

    const {
        data,
        loading,
        error,
        isMultiSeason,
        gaugeValue,
        perJornadaChartData,
        seasonComparisonData,
        seasonBreakdown,
        seasonDelta,
        deltaClass,
        lineChartOptions,
        comparisonChartOptions,
        refresh,
    } = useOccupancy(seasonIdsRef, seasonsRef);

    /** Defensive: si loading persiste >10s, forzar error state */
    const loadingTimedOut = ref(false);
    let loadingTimer = null;
    watch(loading, isLoading => {
        if (isLoading) {
            loadingTimedOut.value = false;
            loadingTimer = setTimeout(() => {
                if (loading.value) {
                    loadingTimedOut.value = true;
                }
            }, 10000);
        } else {
            loadingTimedOut.value = false;
            if (loadingTimer) clearTimeout(loadingTimer);
        }
    });

    onUnmounted(() => {
        if (loadingTimer) clearTimeout(loadingTimer);
    });
</script>
